/**
 * Copyright (c) 2021, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import React, { FunctionComponent, ReactElement, useMemo } from "react";
import { Button, Container, Content, Footer } from "rsuite";
import { AppHeader } from "../components";
import { ASGARDEO_SIGN_UP_FALLBACK_URL, ASGARDEO_TRY_IT_UTM_SOURCE } from "../constants";
import { useConfig } from "../hooks";
import { IdentifiableComponentInterface } from "../models";

/**
 * Application landing page props interface.
 */
export type LandingPropsInterface = IdentifiableComponentInterface;

/**
 * Application landing page.
 *
 * @param {LandingPropsInterface} props - Props injected to the component.
 * @return {React.ReactElement}
 */
export const LandingPage: FunctionComponent<LandingPropsInterface> = (
    props: LandingPropsInterface
): ReactElement => {

    const {
        ["data-componentid"]: componentId
    } = props;

    const { deployment } = useConfig();

    /**
     * Construct website host from the console host and applying utm tags
     * 
     * @returns Asgardeo Sign Up link.
     */
    const websiteHost: string = useMemo(() => {
        if(!deployment?.links?.console) {
            return "";
        }

        const consoleHost: string = deployment?.links?.console;
        
        let url: URL;

        if(consoleHost.includes(".console")) {
            url = new URL(consoleHost.replace(".console", "") + "/signup");
        } else if(consoleHost.includes("console.")) {
            url = new URL(consoleHost.replace("console.", "") + "/signup");
        } else {
            return ASGARDEO_SIGN_UP_FALLBACK_URL;
        }
        
        url.searchParams.set("utm_source", ASGARDEO_TRY_IT_UTM_SOURCE);

        return url.href;
    }, [ deployment ]);

    return (
        <div data-componentid={ componentId }>
            <Container>
                <AppHeader
                    data-componentid="landing-page-app-header"
                    showGoToAsgardeoLink={ true }
                    showSignInButton={ false }
                />
                <Content>
                    <div>
                        <div className="header-landing">
                            <div className="welcome-text-landing-heading">
                                <p>Hey there!</p>
                            </div>
                            <div className="welcome-text-random-landing-content">
                                <p> Need to try out Asgardeo? You&apos;ve landed on the right page </p>
                            </div>
                        </div>
                        <div className="landing-illustration"></div>
                        <p className="body-text-landing">
                            Let&apos;s get registered and try out the login flows of
                            Asgardeo through our Try It app.
                            { /* TODO: Use the divider from the UI framework */ }
                            <br></br>
                            <Button 
                                className="landing-signup-button" 
                                appearance="primary"
                                color="orange"
                                href={ websiteHost }
                                target="_blank"
                            >
                                Sign up  
                            </Button>
                        </p>
                    </div>
                </Content>
                <Footer>
                    <div className="footer">
                        { deployment?.ui?.appCopyright }
                    </div>
                </Footer>
            </Container>
        </div>
    );
};
